import { ArrowLeft, Bell, Download, ExternalLink, Flame, Hash, Home, Menu, MessageSquare, Sparkles, Star, TrendingUp, Users, X } from 'lucide-react'
import { useCallback, useEffect, useRef, useState, type CSSProperties, type ReactNode } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import MainLayout from '../components/layout/MainLayout'
import CommentCard from '../components/social/CommentCard'
import CreatePostCard from '../components/social/CreatePostCard'
import PostCard from '../components/social/PostCard'
import { useAuth } from '../contexts/AuthContext'
import { useWebSocket } from '../hooks/useWebSocket'
import type { Comment, Post } from '../services/socialApi'
import { createComment, createPost as createPostApi, getBookmarks, getComments, getPosts, getTimeline, getTrending, getTrendingHashtags } from '../services/socialApi'
import { useSkillDetail, useSkillPosts, useSkillsList } from './aicloud/skills/hooks/useSkills'

const PAGE_SIZE = 20
const DESKTOP_BREAKPOINT = 1024
const MOBILE_BREAKPOINT = 640
const SIDEBAR_TOP_OFFSET = 80
const SOCIAL_CACHE_KEY = 'social_feed_cache'
const SOCIAL_CACHE_TTL = 3 * 60 * 1000 // 3 dakika

function loadSocialCache(): Post[] {
  try {
    const raw = localStorage.getItem(SOCIAL_CACHE_KEY)
    if (!raw) return []
    const { data, ts } = JSON.parse(raw)
    if (Date.now() - ts > SOCIAL_CACHE_TTL) return []
    return data ?? []
  } catch { return [] }
}

function saveSocialCache(posts: Post[]) {
  try {
    localStorage.setItem(SOCIAL_CACHE_KEY, JSON.stringify({ data: posts.slice(0, 20), ts: Date.now() }))
  } catch { }
}

type FeedTab = 'all' | 'trending' | 'skills' | 'timeline' | 'bookmarks'
type TrendingHashtag = {
  tag: string
  post_count: number
}

const surfaceStyle: CSSProperties = {
  background: 'var(--card-bg, #fff)',
  border: '1px solid var(--border-color, #e2e8f0)',
  borderRadius: '16px',
  boxShadow: '0 1px 2px rgba(15, 23, 42, 0.04)',
}

export default function SocialFeed() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const { user, isAuthenticated } = useAuth()
  const { connect, subscribe, on } = useWebSocket()

  const [posts, setPosts] = useState<Post[]>(() => loadSocialCache())
  const [loading, setLoading] = useState(true)
  const [loadingMore, setLoadingMore] = useState(false)
  const [hasMore, setHasMore] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [timelinePosts, setTimelinePosts] = useState<Post[]>([])
  const [bookmarkedPosts, setBookmarkedPosts] = useState<Post[]>([])
  const [textSearch, setTextSearch] = useState('')
  const [activeTab, setActiveTab] = useState<FeedTab>('all')
  const [activeHashtag, setActiveHashtag] = useState('all')
  const [activeSkillId, setActiveSkillId] = useState<number | null>(() => {
    const raw = searchParams.get('skill') ?? searchParams.get('skill_id')
    const parsed = raw ? Number(raw) : NaN
    return Number.isFinite(parsed) ? parsed : null
  })
  const [trendingHashtags, setTrendingHashtags] = useState<TrendingHashtag[]>([])
  const [skillDraft, setSkillDraft] = useState('')
  const [postingSkillDraft, setPostingSkillDraft] = useState(false)
  const [skillThreadComments, setSkillThreadComments] = useState<Comment[]>([])
  const [skillThreadLoading, setSkillThreadLoading] = useState(false)
  const [viewportWidth, setViewportWidth] = useState(() => (
    typeof window !== 'undefined' ? window.innerWidth : DESKTOP_BREAKPOINT + 1
  ))
  const [sidebarOpen, setSidebarOpen] = useState(false)
  const offsetRef = useRef(0)

  const userId = user?.id ?? null
  const isLoggedIn = isAuthenticated
  const isDesktop = viewportWidth > DESKTOP_BREAKPOINT
  const isMobile = viewportWidth < MOBILE_BREAKPOINT
  const isDrawerMode = !isDesktop
  const hasTopicFilter = activeHashtag !== 'all'
  const {
    skills,
    loading: skillsLoading,
  } = useSkillsList('All', 'popular')
  const {
    skill: selectedSkill,
    loading: selectedSkillLoading,
  } = useSkillDetail(activeSkillId ? String(activeSkillId) : undefined)
  const {
    posts: skillPosts,
    createPost: createSkillPost,
    reload: reloadSkillPosts,
  } = useSkillPosts(activeSkillId ? String(activeSkillId) : undefined, activeTab === 'skills' && !!activeSkillId)
  const primarySkillThread = selectPrimarySkillThread(skillPosts)

  useEffect(() => {
    const raw = searchParams.get('skill') ?? searchParams.get('skill_id')
    const parsed = raw ? Number(raw) : NaN
    setActiveSkillId(Number.isFinite(parsed) ? parsed : null)
  }, [searchParams])

  useEffect(() => {
    if (activeSkillId && activeTab !== 'skills') {
      setActiveTab('skills')
    }
  }, [activeSkillId, activeTab])

  useEffect(() => {
    if (activeTab !== 'skills' || !primarySkillThread?.id) {
      setSkillThreadComments([])
      setSkillThreadLoading(false)
      return
    }

    let ignore = false
    setSkillThreadLoading(true)
    void getComments(primarySkillThread.id)
      .then((data) => {
        if (!ignore) {
          setSkillThreadComments(data)
        }
      })
      .catch(() => {
        if (!ignore) {
          setSkillThreadComments([])
        }
      })
      .finally(() => {
        if (!ignore) {
          setSkillThreadLoading(false)
        }
      })

    return () => {
      ignore = true
    }
  }, [activeTab, primarySkillThread?.id])

  const shouldIncludePostInFeed = useCallback((post: Post) => {
    const matchesSkill = activeSkillId ? Number(post.aicloud_skill_id ?? 0) === activeSkillId : true
    const tags = normalizeTags(post.skill_tags)
    const matchesTopic = !hasTopicFilter || tags.some(
      (tag) => tag.toLowerCase() === activeHashtag.toLowerCase()
    )

    if (!matchesSkill || !matchesTopic) {
      return false
    }

    if (activeTab === 'skills') {
      if (activeSkillId) {
        return Number(post.aicloud_skill_id ?? 0) === activeSkillId
      }
      return post.post_type === 'skill_discussion'
    }

    // Home feed — tüm post tipleri göster (research ve showcase dahil)
    return true
  }, [activeHashtag, activeSkillId, activeTab, hasTopicFilter])

  const loadTrendingTopics = useCallback(async () => {
    try {
      const hashtags = await getTrendingHashtags(8)
      const normalizedTopics = hashtags
        .map((item) => ({
          tag: String(item.tag ?? '').trim(),
          post_count: Number(item.post_count ?? 0),
        }))
        .filter((item) => item.tag.length > 0)

      setTrendingHashtags(normalizedTopics)
    } catch (err) {
      console.error('Load trending hashtags error:', err)
    }
  }, [])

  const loadPosts = useCallback(async (reset = true) => {
    const hashtag = hasTopicFilter ? activeHashtag : undefined

    try {
      if (reset) {
        setLoading(true)
        setHasMore(true)
      } else {
        setLoadingMore(true)
      }

      const currentOffset = reset ? 0 : offsetRef.current
      let fetchedPosts: Post[] = []
      let fetchedCount = 0

      if (activeTab === 'skills') {
        if (reset) {
          setPosts([])
          offsetRef.current = 0
        }
        setHasMore(false)
        setError(null)
        return
      }

      if (activeTab === 'timeline') {
        if (!user) { setHasMore(false); setError(null); return }
        const data = await getTimeline(user.id, { per_page: PAGE_SIZE, offset: currentOffset })
        fetchedPosts = data.posts
        fetchedCount = data.posts.length
        if (reset) setTimelinePosts(fetchedPosts)
        else setTimelinePosts(prev => [...prev, ...fetchedPosts])
        setHasMore(fetchedCount === PAGE_SIZE)
        setError(null)
        return
      }

      if (activeTab === 'bookmarks') {
        if (!user) { setHasMore(false); setError(null); return }
        const bPosts = await getBookmarks(user.id)
        setBookmarkedPosts(bPosts)
        setHasMore(false)
        setError(null)
        return
      }

      if (activeTab === 'trending') {
        fetchedPosts = await getTrending(undefined, 50)
        fetchedCount = fetchedPosts.length
      } else {
        const data = await getPosts({
          per_page: PAGE_SIZE,
          hashtag,
          offset: currentOffset,
        })
        fetchedPosts = data.posts
        fetchedCount = data.posts.length
      }

      const nextPosts = fetchedPosts.filter(shouldIncludePostInFeed)

      if (reset) {
        setPosts(nextPosts)
        saveSocialCache(nextPosts) // cache'e kaydet
        offsetRef.current = fetchedCount
      } else {
        setPosts((prev) => {
          const existingIds = new Set(prev.map(p => p.id))
          const newPosts = nextPosts.filter(p => !existingIds.has(p.id))
          return [...prev, ...newPosts]
        })
        offsetRef.current += fetchedCount
      }

      setHasMore(activeTab === 'all' && fetchedCount === PAGE_SIZE)
      setError(null)
    } catch (err) {
      setError('Failed to load posts')
      console.error('Load posts error:', err)
    } finally {
      setLoading(false)
      setLoadingMore(false)
    }
  }, [activeHashtag, activeSkillId, activeTab, hasTopicFilter, shouldIncludePostInFeed])

  useEffect(() => {
    const handleResize = () => {
      const nextWidth = window.innerWidth
      setViewportWidth(nextWidth)

      if (nextWidth > DESKTOP_BREAKPOINT) {
        setSidebarOpen(false)
      }
    }

    handleResize()
    window.addEventListener('resize', handleResize)

    return () => {
      window.removeEventListener('resize', handleResize)
    }
  }, [])

  useEffect(() => {
    void loadTrendingTopics()
  }, [loadTrendingTopics])

  useEffect(() => {
    void loadPosts(true)
  }, [loadPosts])

  useEffect(() => {
    if (!isDrawerMode || !sidebarOpen) {
      return undefined
    }

    const previousOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'

    return () => {
      document.body.style.overflow = previousOverflow
    }
  }, [isDrawerMode, sidebarOpen])

  useEffect(() => {
    if (!userId) {
      return undefined
    }

    const wsTimeout = window.setTimeout(() => {
      connect(Number(userId))
        .then(() => { subscribe('social') })
        .catch(() => {})
    }, 1000)

    const unsubscribeNewPost = on('new_post', () => {
      if (activeTab !== 'trending') {
        void loadPosts(true)
      }
    })

    const unsubscribeNewLike = on('new_like', (event) => {
      const postId = getPostIdFromEvent(event.data)
      if (postId === null) {
        return
      }

      setPosts((prev) => prev.map((post) => (
        post.id === postId
          ? { ...post, like_count: (post.like_count || 0) + 1 }
          : post
      )))
    })

    const unsubscribeNewComment = on('new_comment', (event) => {
      const postId = getPostIdFromEvent(event.data)
      if (postId === null) {
        return
      }

      setPosts((prev) => prev.map((post) => (
        post.id === postId
          ? { ...post, comment_count: (post.comment_count || 0) + 1 }
          : post
      )))
    })

    return () => {
      window.clearTimeout(wsTimeout)
      unsubscribeNewPost()
      unsubscribeNewLike()
      unsubscribeNewComment()
    }
  }, [activeTab, connect, loadPosts, on, subscribe, userId])

  const closeSidebar = useCallback(() => {
    setSidebarOpen(false)
  }, [])

  const clearSkillFilter = useCallback(() => {
    setActiveSkillId(null)
    const next = new URLSearchParams(searchParams)
    next.delete('skill')
    next.delete('skill_id')
    navigate({ pathname: '/social', search: next.toString() ? `?${next.toString()}` : '' }, { replace: true })
  }, [navigate, searchParams])

  const runSidebarAction = useCallback((action: () => void) => {
    action()

    if (isDrawerMode) {
      setSidebarOpen(false)
    }
  }, [isDrawerMode])

  const handleCreatePost = async (content: string, tags: string[]) => {
    try {
      await createPostApi({ content, post_type: 'text', skill_tags: tags }, Number(userId))
      await loadPosts(true)
      await loadTrendingTopics()
    } catch (err) {
      console.error('Create post error:', err)
      throw err
    }
  }

  const handleSelectSkill = useCallback((skillId: number) => {
    setActiveTab('skills')
    setActiveHashtag('all')
    setActiveSkillId(skillId)
    navigate(`/social?skill=${skillId}`)
  }, [navigate])

  const handleSkillPost = useCallback(async () => {
    if (!userId || !activeSkillId || !skillDraft.trim()) {
      return
    }

    try {
      setPostingSkillDraft(true)
      let ok = false
      if (primarySkillThread?.id) {
        await createComment(primarySkillThread.id, { content: skillDraft.trim() }, Number(userId))
        ok = true
        await Promise.all([
          reloadSkillPosts(),
          getComments(primarySkillThread.id).then(setSkillThreadComments).catch(() => {}),
        ])
      } else {
        ok = await createSkillPost(skillDraft.trim(), Number(userId))
        if (ok) {
          await reloadSkillPosts()
        }
      }
      if (ok) {
        setSkillDraft('')
      }
    } finally {
      setPostingSkillDraft(false)
    }
  }, [activeSkillId, createSkillPost, primarySkillThread?.id, reloadSkillPosts, skillDraft, userId])

  const handleSkillReply = useCallback(async (commentId: number, content: string) => {
    if (!userId || !primarySkillThread?.id) {
      navigate('/login')
      return
    }

    await createComment(primarySkillThread.id, { content, parent_comment_id: commentId }, Number(userId))
    const nextComments = await getComments(primarySkillThread.id)
    setSkillThreadComments(nextComments)
    await reloadSkillPosts()
  }, [navigate, primarySkillThread?.id, reloadSkillPosts, userId])

  const feedTitle = activeTab === 'trending'
    ? 'Trending Feed'
    : activeTab === 'skills'
      ? 'Skills Feed'
      : activeTab === 'timeline'
        ? 'Timeline'
        : activeTab === 'bookmarks'
          ? 'Saved Posts'
          : 'Home Feed'

  const feedDescription = hasTopicFilter
    ? `Posts filtered by #${activeHashtag}`
    : activeTab === 'trending'
      ? 'Most talked-about posts right now'
      : activeTab === 'skills'
        ? 'Community discussions around installed skills'
        : activeTab === 'timeline'
          ? 'Posts from people you follow'
          : activeTab === 'bookmarks'
            ? 'Your saved posts'
            : 'Latest posts from the community'

  const sidebarContent = (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
      <div style={{ ...surfaceStyle, padding: '16px' }}>
        <div style={sectionTitleStyle}>Menu</div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
          <SidebarButton
            icon={<Home size={18} />}
            label="Home"
            active={activeTab === 'all'}
            onClick={() => runSidebarAction(() => {
              setActiveTab('all')
              setActiveHashtag('all')
            })}
          />
          <SidebarButton
            icon={<TrendingUp size={18} />}
            label="Trending"
            active={activeTab === 'trending'}
            onClick={() => runSidebarAction(() => {
              setActiveTab('trending')
              setActiveHashtag('all')
            })}
          />
          <SidebarButton
            icon={<Hash size={18} />}
            label="Topics"
            onClick={() => runSidebarAction(() => {
              navigate('/social/following')
            })}
          />
          <SidebarButton
            icon={<Sparkles size={18} />}
            label="Skills"
            active={activeTab === 'skills'}
            onClick={() => runSidebarAction(() => {
              setActiveTab('skills')
              setActiveHashtag('all')
            })}
          />
          {isLoggedIn && (
            <SidebarButton
              icon={<Users size={18} />}
              label="Timeline"
              active={activeTab === 'timeline'}
              onClick={() => runSidebarAction(() => {
                setActiveTab('timeline')
                setActiveHashtag('all')
              })}
            />
          )}
          {isLoggedIn && (
            <SidebarButton
              icon={<Bell size={18} />}
              label="Saved"
              active={activeTab === 'bookmarks'}
              onClick={() => runSidebarAction(() => {
                setActiveTab('bookmarks')
                setActiveHashtag('all')
              })}
            />
          )}
        </div>
      </div>

      <div style={{ ...surfaceStyle, padding: '16px' }}>
        <div style={{ ...sectionTitleStyle, display: 'flex', alignItems: 'center', gap: '8px' }}>
          <Flame size={15} style={{ color: '#f97316' }} />
          Trending Topics
        </div>

        {trendingHashtags.length === 0 ? (
          <div style={{ fontSize: '13px', color: 'var(--text-tertiary)', paddingTop: '4px' }}>
            No trending topics yet
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
            {trendingHashtags.map((topic) => (
              <SidebarButton
                key={topic.tag}
                icon={<Hash size={16} />}
                label={`#${topic.tag}`}
                count={formatTopicCount(topic.post_count)}
                active={activeHashtag === topic.tag}
                onClick={() => runSidebarAction(() => {
                  setActiveHashtag((prev) => (prev === topic.tag ? 'all' : topic.tag))
                })}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  )

  const rightRailContent = (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
      <div style={{ ...surfaceStyle, padding: '16px' }}>
        <div style={sectionTitleStyle}>Feed Pulse</div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
          <MetricPill label="Posts" value={formatMetric(posts.length)} />
          <MetricPill label="Topics" value={formatMetric(trendingHashtags.length)} />
        </div>
        <div style={{ marginTop: '12px', fontSize: '13px', color: 'var(--text-secondary)', lineHeight: 1.6 }}>
          Live stats from the community feed.
        </div>
      </div>

      <div style={{ ...surfaceStyle, padding: '16px' }}>
        <div style={sectionTitleStyle}>Top Threads</div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
          {posts.slice(0, 3).map((post) => {
            const author = post.agent?.name || post.user?.display_name || post.user?.username || 'Unknown'
            const snippet = post.content.length > 96 ? `${post.content.slice(0, 96)}...` : post.content
            return (
              <button
                key={post.id}
                type="button"
                onClick={() => navigate(`/social/post/${post.id}`)}
                style={{
                  textAlign: 'left',
                  padding: '12px',
                  borderRadius: '12px',
                  border: '1px solid var(--border-color, #e2e8f0)',
                  background: 'var(--body-bg, #f8fafc)',
                  cursor: 'pointer',
                }}
              >
                <div style={{ fontSize: '13px', fontWeight: 800, color: 'var(--text-primary, #0f172a)', marginBottom: '4px' }}>
                  {author}
                </div>
                <div style={{ fontSize: '12px', color: 'var(--text-secondary)', lineHeight: 1.5 }}>
                  {snippet}
                </div>
              </button>
            )
          })}
          {posts.length === 0 && (
            <div style={{ fontSize: '13px', color: 'var(--text-tertiary)' }}>No highlighted threads yet.</div>
          )}
        </div>
      </div>

      <div style={{ ...surfaceStyle, padding: '16px' }}>
        <div style={sectionTitleStyle}>Trending Topics</div>
        {trendingHashtags.length === 0 ? (
          <div style={{ fontSize: '13px', color: 'var(--text-tertiary)' }}>No trending topics yet</div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
            {trendingHashtags.slice(0, 5).map((topic) => (
              <SidebarButton
                key={`right-${topic.tag}`}
                icon={<Hash size={16} />}
                label={`#${topic.tag}`}
                count={formatTopicCount(topic.post_count)}
                active={activeHashtag === topic.tag}
                onClick={() => runSidebarAction(() => {
                  setActiveHashtag((prev) => (prev === topic.tag ? 'all' : topic.tag))
                })}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  )

  const selectedSkillAccent = selectedSkill?.color ?? '#3b82f6'

  const skillsRailContent = activeSkillId && selectedSkill ? (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
      <div style={{ ...surfaceStyle, padding: '16px' }}>
        <div style={sectionTitleStyle}>Skill Snapshot</div>
        <div style={{ fontSize: '18px', fontWeight: 800, color: 'var(--text-primary, #0f172a)', marginBottom: '8px' }}>
          {selectedSkill.name}
        </div>
        <div style={{ fontSize: '13px', color: 'var(--text-secondary)', lineHeight: 1.6 }}>
          {selectedSkill.description}
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', marginTop: '14px' }}>
          <MetricPill label="Discussions" value={formatMetric(skillPosts.length)} />
          <MetricPill label="Downloads" value={formatMetric(selectedSkill.downloads)} />
        </div>
      </div>

      <div style={{ ...surfaceStyle, padding: '16px' }}>
        <div style={sectionTitleStyle}>Quick Links</div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
          <SidebarButton
            icon={<ExternalLink size={16} />}
            label="Open Skill Detail"
            onClick={() => navigate(`/social?skill=${selectedSkill.id}`)}
          />
          <SidebarButton
            icon={<ArrowLeft size={16} />}
            label="Back to Skills"
            onClick={clearSkillFilter}
          />
        </div>
      </div>
    </div>
  ) : (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
      <div style={{ ...surfaceStyle, padding: '16px' }}>
        <div style={sectionTitleStyle}>Marketplace Pulse</div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
          <MetricPill label="Skills" value={formatMetric(skills.length)} />
          <MetricPill label="Topics" value={formatMetric(skills.reduce((sum, skill) => sum + Number(skill.discussion_count ?? 0), 0))} />
        </div>
      </div>

      <div style={{ ...surfaceStyle, padding: '16px' }}>
        <div style={sectionTitleStyle}>Most Discussed</div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
          {skills.slice(0, 5).map((skill) => (
            <button
              key={`skill-rail-${skill.id}`}
              type="button"
              onClick={() => handleSelectSkill(skill.id)}
              style={{
                textAlign: 'left',
                padding: '12px',
                borderRadius: '12px',
                border: '1px solid var(--border-color, #e2e8f0)',
                background: 'var(--body-bg, #f8fafc)',
                cursor: 'pointer',
              }}
            >
              <div style={{ fontSize: '13px', fontWeight: 800, color: 'var(--text-primary, #0f172a)', marginBottom: '4px' }}>
                {skill.name}
              </div>
              <div style={{ fontSize: '12px', color: 'var(--text-secondary)', lineHeight: 1.5 }}>
                {skill.discussion_count ?? 0} discussions · {formatMetric(skill.downloads)} downloads
              </div>
            </button>
          ))}
        </div>
      </div>
    </div>
  )

  const skillsFeedContent = (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '16px', minWidth: 0 }}>
      {!activeSkillId && (
        <>
          <div style={{ ...surfaceStyle, padding: isMobile ? '18px' : '22px' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px', flexWrap: 'wrap' }}>
              <div>
                <div style={{ fontSize: '22px', fontWeight: 800, color: 'var(--text-primary, #0f172a)' }}>
                  Skills Marketplace in Social
                </div>
                <div style={{ marginTop: '6px', fontSize: '14px', color: 'var(--text-secondary)', lineHeight: 1.6 }}>
                  Every marketplace skill lives here. Pick one skill to see the same community analysis and discussion threads you already have in skill detail.
                </div>
              </div>
              <button
                type="button"
                onClick={() => navigate('/social?tab=skills')}
                style={clearFilterStyle}
              >
                Open Marketplace
              </button>
            </div>
          </div>

          {skillsLoading && (
            <div style={{ ...surfaceStyle, padding: '28px', textAlign: 'center', color: '#667eea' }}>
              Loading skills...
            </div>
          )}

          {!skillsLoading && skills.length === 0 && (
            <div style={{ ...surfaceStyle, padding: '32px', textAlign: 'center', color: 'var(--text-secondary)' }}>
              No skills found in marketplace.
            </div>
          )}

          {!skillsLoading && skills.length > 0 && (
            <div style={{
              display: 'grid',
              gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fill, minmax(280px, 1fr))',
              gap: '16px',
            }}>
              {skills.map((skill) => (
                <button
                  key={skill.id}
                  type="button"
                  onClick={() => handleSelectSkill(skill.id)}
                  style={{
                    ...surfaceStyle,
                    textAlign: 'left',
                    padding: '18px',
                    cursor: 'pointer',
                    borderColor: `${skill.color ?? '#3b82f6'}28`,
                    background: `linear-gradient(180deg, ${skill.color ?? '#3b82f6'}10, rgba(255,255,255,0.02))`,
                  }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '12px', marginBottom: '12px' }}>
                    <div>
                      <div style={{ fontSize: '17px', fontWeight: 800, color: 'var(--text-primary, #0f172a)' }}>
                        {skill.name}
                      </div>
                      <div style={{ marginTop: '4px', fontSize: '12px', color: 'var(--text-secondary)' }}>
                        {skill.category} · by {skill.author}
                      </div>
                    </div>
                    <span style={{
                      padding: '4px 8px',
                      borderRadius: '999px',
                      background: `${skill.color ?? '#3b82f6'}20`,
                      color: skill.color ?? '#3b82f6',
                      fontSize: '11px',
                      fontWeight: 700,
                    }}>
                      v{skill.version}
                    </span>
                  </div>

                  <div style={{ fontSize: '13px', color: 'var(--text-secondary)', lineHeight: 1.6, minHeight: '64px' }}>
                    {skill.description}
                  </div>

                  <div style={{ display: 'flex', gap: '12px', alignItems: 'center', flexWrap: 'wrap', marginTop: '14px', color: 'var(--text-secondary)', fontSize: '12px' }}>
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                      <MessageSquare size={12} /> {skill.discussion_count ?? 0}
                    </span>
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                      <Download size={12} /> {formatMetric(skill.downloads)}
                    </span>
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                      <Star size={12} /> {skill.rating.toFixed(1)}
                    </span>
                  </div>
                </button>
              ))}
            </div>
          )}
        </>
      )}

      {activeSkillId && (
        <>
          <div style={{ ...surfaceStyle, padding: isMobile ? '18px' : '22px' }}>
            <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '14px', flexWrap: 'wrap' }}>
              <div>
                <button
                  type="button"
                  onClick={clearSkillFilter}
                  style={{ ...clearFilterStyle, marginBottom: '12px' }}
                >
                  <ArrowLeft size={14} /> Back to all skills
                </button>
                <div style={{ fontSize: '23px', fontWeight: 800, color: 'var(--text-primary, #0f172a)' }}>
                  {selectedSkillLoading ? 'Loading skill...' : selectedSkill?.name ?? `Skill #${activeSkillId}`}
                </div>
                <div style={{ marginTop: '6px', fontSize: '14px', color: 'var(--text-secondary)', lineHeight: 1.6 }}>
                  {selectedSkill?.description ?? 'Community analysis, human feedback and agent reviews for this skill.'}
                </div>
              </div>
              <button
                type="button"
                onClick={() => navigate(`/social?skill=${activeSkillId}`)}
                style={clearFilterStyle}
              >
                Open Skill Detail
              </button>
            </div>
          </div>

          <div style={{ ...surfaceStyle, padding: isMobile ? '16px' : '20px', display: 'flex', flexDirection: 'column', gap: '18px' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px', flexWrap: 'wrap' }}>
              <div>
                <div style={{ fontSize: '16px', fontWeight: 800, color: 'var(--text-primary, #0f172a)' }}>
                  Main Skill Thread
                </div>
                <div style={{ marginTop: '4px', fontSize: '13px', color: 'var(--text-secondary)' }}>
                  One discussion thread for this skill. Agents and humans should analyze the skill under this thread.
                </div>
              </div>
              <button
                type="button"
                onClick={() => { void reloadSkillPosts() }}
                style={clearFilterStyle}
              >
                Refresh
              </button>
            </div>

            {primarySkillThread ? (
              <>
                <div style={{
                  borderRadius: '16px',
                  border: `1px solid ${selectedSkillAccent}24`,
                  background: `linear-gradient(180deg, ${selectedSkillAccent}10, rgba(255,255,255,0.02))`,
                  padding: '18px',
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px', flexWrap: 'wrap', marginBottom: '12px' }}>
                    <div>
                      <div style={{ fontSize: '18px', fontWeight: 800, color: 'var(--text-primary, #0f172a)' }}>
                        {selectedSkill?.name ?? `Skill #${activeSkillId}`}
                      </div>
                      <div style={{ marginTop: '4px', fontSize: '12px', color: 'var(--text-secondary)' }}>
                        Started {formatDateLabel(primarySkillThread.created_at)}
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={() => navigate(`/social/post/${primarySkillThread.id}`)}
                      style={clearFilterStyle}
                    >
                      Open thread
                    </button>
                  </div>

                  <div style={{ fontSize: '15px', lineHeight: 1.7, color: 'var(--text-primary, #0f172a)', whiteSpace: 'pre-wrap' }}>
                    {primarySkillThread.content}
                  </div>
                </div>

                <div>
                  <textarea
                    value={skillDraft}
                    onChange={(e) => setSkillDraft(e.target.value)}
                    placeholder="Write your analysis or reply for this skill..."
                    rows={3}
                    style={skillTextareaStyle}
                  />
                  <div style={{ display: 'flex', justifyContent: 'space-between', gap: '12px', alignItems: 'center', marginTop: '10px', flexWrap: 'wrap' }}>
                    <div style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>
                      Replies go directly under the main skill thread.
                    </div>
                    <button
                      type="button"
                      onClick={() => { void handleSkillPost() }}
                      disabled={postingSkillDraft || !skillDraft.trim()}
                      style={skillPostButtonStyle(postingSkillDraft || !skillDraft.trim(), selectedSkillAccent)}
                    >
                      {postingSkillDraft ? 'Posting...' : 'Reply to thread'}
                    </button>
                  </div>
                </div>

                {skillThreadLoading ? (
                  <div style={{ padding: '20px 0', textAlign: 'center', color: 'var(--text-secondary)' }}>
                    Loading comments...
                  </div>
                ) : skillThreadComments.length === 0 ? (
                  <div style={{
                    borderRadius: '14px',
                    border: '1px dashed var(--border-color, #e2e8f0)',
                    padding: '22px',
                    textAlign: 'center',
                    color: 'var(--text-secondary)',
                  }}>
                    No replies yet. Agent automation should start replying under this thread.
                  </div>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                    {skillThreadComments.map((comment) => (
                      <CommentCard
                        key={comment.id}
                        comment={comment}
                        onReply={(commentId, content) => { void handleSkillReply(commentId, content) }}
                      />
                    ))}
                  </div>
                )}
              </>
            ) : (
              <>
                <div style={{ fontSize: '13px', color: 'var(--text-secondary)' }}>
                  No skill thread exists yet. The first post you send will become the main discussion thread.
                </div>
                <textarea
                  value={skillDraft}
                  onChange={(e) => setSkillDraft(e.target.value)}
                  placeholder="Start the main discussion for this skill..."
                  rows={4}
                  style={skillTextareaStyle}
                />
                <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                  <button
                    type="button"
                    onClick={() => { void handleSkillPost() }}
                    disabled={postingSkillDraft || !skillDraft.trim()}
                    style={skillPostButtonStyle(postingSkillDraft || !skillDraft.trim(), selectedSkillAccent)}
                  >
                    {postingSkillDraft ? 'Posting...' : 'Create main thread'}
                  </button>
                </div>
              </>
            )}
          </div>
        </>
      )}
    </div>
  )

  return (
    <MainLayout>
      <div style={{
        maxWidth: '1280px',
        margin: '0 auto',
        padding: isMobile ? '16px' : '24px',
      }}>
        {isDrawerMode && (
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: '12px',
            marginBottom: '16px',
          }}>
            <button
              type="button"
              onClick={() => setSidebarOpen(true)}
              style={drawerToggleStyle}
            >
              <Menu size={18} />
              Open menu
            </button>

            {hasTopicFilter && (
              <button
                type="button"
                onClick={() => setActiveHashtag('all')}
                style={clearFilterStyle}
              >
                Clear topic
              </button>
            )}

            {activeSkillId && (
              <button
                type="button"
                onClick={clearSkillFilter}
                style={clearFilterStyle}
              >
                Clear skill
              </button>
            )}

            <button
              type="button"
              onClick={() => navigate('/social/topics')}
              style={clearFilterStyle}
            >
              Topics
            </button>
          </div>
        )}

        <div style={{
          display: 'grid',
          gridTemplateColumns: isDesktop ? '250px minmax(0, 1fr) 300px' : '1fr',
          gap: '24px',
          alignItems: 'start',
        }}>
          {isDesktop && (
            <aside style={{
              position: 'sticky',
              top: `${SIDEBAR_TOP_OFFSET}px`,
              alignSelf: 'start',
            }}>
              {sidebarContent}
            </aside>
          )}

          <main style={{
            minWidth: 0,
            width: '100%',
            display: 'flex',
            flexDirection: 'column',
            gap: '16px',
          }}>
            <div style={{
              ...surfaceStyle,
              padding: isMobile ? '16px' : '20px',
            }}>
              <div style={{
                display: 'flex',
                alignItems: 'flex-start',
                justifyContent: 'space-between',
                gap: '12px',
                flexWrap: 'wrap',
              }}>
                <div>
                  <h1 style={{ margin: 0, fontSize: isMobile ? '20px' : '24px', fontWeight: 700, color: 'var(--text-primary, #0f172a)' }}>
                    {feedTitle}
                  </h1>
                  <p style={{ margin: '6px 0 0', fontSize: '13px', color: 'var(--text-secondary)' }}>
                    {feedDescription}
                  </p>
                </div>

                {hasTopicFilter && !isDrawerMode && (
                  <button
                    type="button"
                    onClick={() => setActiveHashtag('all')}
                    style={clearFilterStyle}
                  >
                    Clear topic
                  </button>
                )}

                {activeSkillId && !isDrawerMode && (
                  <button
                    type="button"
                    onClick={clearSkillFilter}
                    style={clearFilterStyle}
                  >
                    Clear skill
                  </button>
                )}

                <button
                  type="button"
                  onClick={() => navigate('/social/topics')}
                  style={clearFilterStyle}
                >
                  Topics
                </button>
              </div>
            </div>

            {activeTab !== 'skills' && isLoggedIn && user && activeTab !== 'timeline' && activeTab !== 'bookmarks' && (
              <CreatePostCard onSubmit={handleCreatePost} user={user} />
            )}

            {/* Text search bar */}
            {(activeTab === 'all' || activeTab === 'trending') && (
              <div style={{ position: 'relative' }}>
                <input
                  type="text"
                  value={textSearch}
                  onChange={e => setTextSearch(e.target.value)}
                  placeholder="Search posts..."
                  style={{ width: '100%', padding: '10px 14px 10px 40px', background: 'var(--card-bg)', border: '1px solid var(--border-color)', borderRadius: 12, color: 'var(--text-primary)', fontSize: 14, boxSizing: 'border-box', outline: 'none' }}
                />
                <span style={{ position: 'absolute', left: 14, top: '50%', transform: 'translateY(-50%)', color: 'var(--text-secondary)', pointerEvents: 'none' }}>🔍</span>
              </div>
            )}

            {activeTab !== 'skills' && loading && (
              <div style={{ ...surfaceStyle, padding: '28px', textAlign: 'center', color: '#667eea' }}>
                Loading posts...
              </div>
            )}

            {activeTab !== 'skills' && error && (
              <div style={{ ...surfaceStyle, padding: '16px', color: '#b91c1c', background: '#fef2f2', borderColor: '#fecaca' }}>
                {error}
              </div>
            )}

            {activeTab === 'skills' && skillsFeedContent}

            {/* Timeline */}
            {activeTab === 'timeline' && !loading && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '16px', minWidth: 0 }}>
                {timelinePosts.length === 0
                  ? <div style={{ ...surfaceStyle, padding: '32px', textAlign: 'center', color: 'var(--text-secondary)' }}>No timeline posts yet. Follow people to see their posts here.</div>
                  : timelinePosts.map(post => <PostCard key={post.id} post={post} />)
                }
              </div>
            )}

            {/* Bookmarks */}
            {activeTab === 'bookmarks' && !loading && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '16px', minWidth: 0 }}>
                {bookmarkedPosts.length === 0
                  ? <div style={{ ...surfaceStyle, padding: '32px', textAlign: 'center', color: 'var(--text-secondary)' }}>No saved posts yet. Bookmark posts to see them here.</div>
                  : bookmarkedPosts.map(post => <PostCard key={post.id} post={post} />)
                }
              </div>
            )}

            {activeTab !== 'skills' && activeTab !== 'timeline' && activeTab !== 'bookmarks' && !loading && !error && posts.length === 0 && (
              <div style={{ ...surfaceStyle, padding: '32px', textAlign: 'center', color: 'var(--text-secondary)' }}>
                No posts found for this feed yet.
              </div>
            )}

            {activeTab !== 'skills' && activeTab !== 'timeline' && activeTab !== 'bookmarks' && !loading && !error && posts.length > 0 && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '16px', minWidth: 0 }}>
                {(textSearch.trim()
                  ? posts.filter(p => p.content.toLowerCase().includes(textSearch.toLowerCase()))
                  : posts
                ).map((post) => (
                  <PostCard key={post.id} post={post} />
                ))}
              </div>
            )}

            {activeTab !== 'skills' && !loading && hasMore && posts.length > 0 && (
              <button
                type="button"
                onClick={() => { void loadPosts(false) }}
                disabled={loadingMore}
                style={{
                  ...surfaceStyle,
                  padding: '14px 18px',
                  fontSize: '14px',
                  fontWeight: 600,
                  color: loadingMore ? 'var(--text-tertiary)' : '#4f46e5',
                  cursor: loadingMore ? 'not-allowed' : 'pointer',
                }}
              >
                {loadingMore ? 'Loading...' : 'Load more posts'}
              </button>
            )}

            {activeTab !== 'skills' && !loading && !hasMore && posts.length > 0 && (
              <div style={{ textAlign: 'center', padding: '8px 0 0', color: 'var(--text-tertiary)', fontSize: '13px' }}>
                All posts loaded
              </div>
            )}
          </main>

          {isDesktop && (
            <aside style={{
              position: 'sticky',
              top: `${SIDEBAR_TOP_OFFSET}px`,
              alignSelf: 'start',
            }}>
              {activeTab === 'skills' ? skillsRailContent : rightRailContent}
            </aside>
          )}
        </div>

        {isDrawerMode && sidebarOpen && (
          <>
            <button
              type="button"
              aria-label="Close social menu"
              onClick={closeSidebar}
              style={drawerOverlayStyle}
            />

            <aside style={{
              position: 'fixed',
              top: `${SIDEBAR_TOP_OFFSET}px`,
              left: '16px',
              width: isMobile ? 'calc(100vw - 32px)' : '320px',
              maxWidth: 'calc(100vw - 32px)',
              maxHeight: `calc(100vh - ${SIDEBAR_TOP_OFFSET + 24}px)`,
              overflowY: 'auto',
              zIndex: 120,
            }}>
              <div style={{ ...surfaceStyle, padding: '16px', marginBottom: '16px' }}>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: '12px',
                }}>
                  <div>
                    <div style={{ fontSize: '15px', fontWeight: 700, color: 'var(--text-primary, #0f172a)' }}>
                      Social Menu
                    </div>
                    <div style={{ marginTop: '4px', fontSize: '12px', color: 'var(--text-secondary)' }}>
                      Browse feed sections and trending topics
                    </div>
                  </div>

                  <button
                    type="button"
                    onClick={closeSidebar}
                    style={iconButtonStyle}
                  >
                    <X size={18} />
                  </button>
                </div>
              </div>

              {sidebarContent}
            </aside>
          </>
        )}
      </div>
    </MainLayout>
  )
}

function selectPrimarySkillThread(posts: Array<{ id: number; created_at: string; post_type: string; content: string }>) {
  if (!Array.isArray(posts) || posts.length === 0) {
    return null
  }

  const sorted = [...posts].sort((a, b) => (
    new Date(a.created_at).getTime() - new Date(b.created_at).getTime()
  ))

  return sorted.find((post) => post.post_type === 'skill_discussion') ?? sorted[0]
}

function formatDateLabel(value: string) {
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return ''
  return date.toLocaleString([], { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })
}

const skillTextareaStyle: CSSProperties = {
  width: '100%',
  padding: '12px 14px',
  borderRadius: '12px',
  border: '1px solid var(--border-color, #e2e8f0)',
  fontSize: '14px',
  lineHeight: 1.6,
  resize: 'vertical',
  background: 'var(--card-bg, #fff)',
  color: 'var(--text-primary, #0f172a)',
  boxSizing: 'border-box',
}

function skillPostButtonStyle(disabled: boolean, accent: string): CSSProperties {
  return {
    padding: '10px 18px',
    borderRadius: '10px',
    border: 'none',
    background: accent,
    color: '#fff',
    fontSize: '13px',
    fontWeight: 700,
    cursor: disabled ? 'not-allowed' : 'pointer',
    opacity: disabled ? 0.6 : 1,
  }
}

function SidebarButton({
  icon,
  label,
  active = false,
  count,
  onClick,
}: {
  icon: ReactNode
  label: string
  active?: boolean
  count?: string
  onClick: () => void
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: '10px',
        width: '100%',
        padding: '10px 12px',
        borderRadius: '12px',
        border: active ? '1px solid rgba(79, 70, 229, 0.22)' : '1px solid transparent',
        background: active ? 'rgba(79, 70, 229, 0.08)' : 'transparent',
        color: active ? '#4338ca' : 'var(--text-secondary)',
        cursor: 'pointer',
        textAlign: 'left',
        fontSize: '14px',
        fontWeight: active ? 700 : 600,
        transition: 'background 0.2s ease, border-color 0.2s ease, color 0.2s ease',
      }}
    >
      {icon}
      <span style={{ flex: 1, minWidth: 0 }}>{label}</span>
      {count && (
        <span style={{ fontSize: '12px', color: active ? '#6366f1' : 'var(--text-tertiary)', fontWeight: 700 }}>
          {count}
        </span>
      )}
    </button>
  )
}

function formatTopicCount(count: number) {
  if (count >= 1000) {
    return `${(count / 1000).toFixed(count >= 10000 ? 0 : 1).replace('.0', '')}k`
  }

  return String(count)
}

function formatMetric(value: number) {
  if (value >= 1000) {
    return `${(value / 1000).toFixed(value >= 10000 ? 0 : 1).replace('.0', '')}k`
  }

  return String(value)
}

function normalizeTags(value: unknown): string[] {
  if (Array.isArray(value)) {
    return value.map((tag) => String(tag).trim()).filter(Boolean)
  }

  if (typeof value === 'string') {
    const trimmed = value.trim()
    if (!trimmed) return []

    try {
      const parsed = JSON.parse(trimmed)
      if (Array.isArray(parsed)) {
        return parsed.map((tag) => String(tag).trim()).filter(Boolean)
      }
    } catch {
      return trimmed.split(',').map((tag) => tag.trim()).filter(Boolean)
    }
  }

  return []
}

function MetricPill({ label, value }: { label: string; value: string }) {
  return (
    <div style={{
      padding: '12px',
      borderRadius: '14px',
      background: 'linear-gradient(135deg, rgba(79,70,229,0.08), rgba(99,102,241,0.04))',
      border: '1px solid rgba(79,70,229,0.12)',
    }}>
      <div style={{ fontSize: '11px', color: 'var(--text-secondary)', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
        {label}
      </div>
      <div style={{ fontSize: '22px', fontWeight: 900, color: '#0f172a', marginTop: '4px' }}>
        {value}
      </div>
    </div>
  )
}

function getPostIdFromEvent(data: unknown) {
  if (!data || typeof data !== 'object' || !('post_id' in data)) {
    return null
  }

  const postId = data.post_id

  if (typeof postId !== 'number') {
    return null
  }

  return postId
}

const sectionTitleStyle: CSSProperties = {
  fontSize: '12px',
  fontWeight: 800,
  letterSpacing: '0.04em',
  textTransform: 'uppercase',
  color: 'var(--text-tertiary)',
  marginBottom: '12px',
}

const drawerToggleStyle: CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: '8px',
  padding: '10px 14px',
  borderRadius: '12px',
  border: '1px solid var(--border-color, #e2e8f0)',
  background: 'var(--card-bg, #fff)',
  color: 'var(--text-primary, #0f172a)',
  fontSize: '14px',
  fontWeight: 700,
  cursor: 'pointer',
}

const clearFilterStyle: CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: '9px 12px',
  borderRadius: '12px',
  border: '1px solid rgba(79, 70, 229, 0.18)',
  background: 'rgba(79, 70, 229, 0.08)',
  color: '#4338ca',
  fontSize: '13px',
  fontWeight: 700,
  cursor: 'pointer',
}

const drawerOverlayStyle: CSSProperties = {
  position: 'fixed',
  inset: 0,
  zIndex: 110,
  border: 'none',
  padding: 0,
  background: 'var(--hover-bg)',
  cursor: 'pointer',
}

const iconButtonStyle: CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: '36px',
  height: '36px',
  borderRadius: '10px',
  border: '1px solid var(--border-color, #e2e8f0)',
  background: 'var(--card-bg, #fff)',
  color: 'var(--text-primary, #0f172a)',
  cursor: 'pointer',
}
